import { useEffect, useRef, useState, useCallback } from 'react';

interface UseIdleTimeoutOptions {
  /**
   * Tiempo de inactividad en milisegundos antes de mostrar la advertencia
   * @default 900000 (15 minutos)
   */
  idleTime?: number;
  
  /**
   * Tiempo de advertencia en milisegundos antes del logout automático
   * @default 60000 (1 minuto)
   */
  warningTime?: number;
  
  /**
   * Callback que se ejecuta cuando el usuario está inactivo
   */
  onIdle: () => void;
  
  /**
   * Callback que se ejecuta cuando se muestra la advertencia
   */
  onWarning?: () => void;
  
  /**
   * Si está habilitado el timeout
   * @default true
   */
  enabled?: boolean;
}

interface UseIdleTimeoutReturn {
  /** Tiempo restante en segundos antes del logout */
  remainingTime: number;
  /** Si está mostrando la advertencia */
  isWarning: boolean;
  /** Resetear el timer de inactividad */
  resetTimer: () => void;
  /** Extender la sesión (resetear y cerrar advertencia) */
  extendSession: () => void;
}

/**
 * Hook para detectar inactividad del usuario y cerrar sesión automáticamente
 * 
 * @example
 * ```tsx
 * const { isWarning, remainingTime, extendSession } = useIdleTimeout({
 *   idleTime: 15 * 60 * 1000, // 15 minutos
 *   warningTime: 60 * 1000, // 1 minuto
 *   onIdle: () => logout(),
 *   onWarning: () => console.log('Mostrando advertencia')
 * });
 * ```
 */
export const useIdleTimeout = ({
  idleTime = 15 * 60 * 1000, // 15 minutos por defecto
  warningTime = 60 * 1000, // 1 minuto por defecto
  onIdle,
  onWarning,
  enabled = true,
}: UseIdleTimeoutOptions): UseIdleTimeoutReturn => {
  const [isWarning, setIsWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isWarningRef = useRef(false);
  const onIdleRef = useRef(onIdle);
  const onWarningRef = useRef(onWarning);

  // Mantener refs actualizadas
  useEffect(() => {
    onIdleRef.current = onIdle;
    onWarningRef.current = onWarning;
  }, [onIdle, onWarning]);

  const clearAllTimers = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const startCountdown = () => {
    const warningTimeInSeconds = Math.floor(warningTime / 1000);
    setRemainingTime(warningTimeInSeconds);
    
    countdownIntervalRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const resetTimer = useCallback(() => {
    if (!enabled) return;

    clearAllTimers();
    setIsWarning(false);
    isWarningRef.current = false;
    setRemainingTime(0);

    // Timer para mostrar la advertencia
    idleTimerRef.current = setTimeout(() => {
      setIsWarning(true);
      isWarningRef.current = true;
      startCountdown();
      onWarningRef.current?.();

      // Timer para el logout automático
      warningTimerRef.current = setTimeout(() => {
        onIdleRef.current();
      }, warningTime);
    }, idleTime);
  }, [enabled, idleTime, warningTime]);

  const extendSession = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      setIsWarning(false);
      isWarningRef.current = false;
      setRemainingTime(0);
      return;
    }

    // Track when the idle timer was last reset to detect background tab expiry
    const lastResetRef = { current: Date.now() };

    // Eventos que resetean el timer de inactividad
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    const handleActivity = () => {
      // Usar la ref para evitar re-renders
      if (!isWarningRef.current) {
        lastResetRef.current = Date.now();
        resetTimer();
      }
    };

    // Handle tab visibility change — browsers pause timers in background tabs
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tab became visible again — check if idle time has passed while in background
        const elapsed = Date.now() - lastResetRef.current;
        const totalTimeout = idleTime + warningTime;
        
        if (elapsed >= totalTimeout) {
          // Session should have expired while tab was in background — force logout
          onIdleRef.current();
        } else if (elapsed >= idleTime) {
          // Warning period — show warning with remaining time
          const remaining = Math.max(0, Math.floor((totalTimeout - elapsed) / 1000));
          setIsWarning(true);
          isWarningRef.current = true;
          setRemainingTime(remaining);
          onWarningRef.current?.();
          
          // Set timer for the remaining warning time
          warningTimerRef.current = setTimeout(() => {
            onIdleRef.current();
          }, remaining * 1000);
        } else {
          // Still within idle period — reset timer normally
          lastResetRef.current = Date.now();
          resetTimer();
        }
      }
    };

    // Agregar listeners
    events.forEach((event) => {
      document.addEventListener(event, handleActivity);
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Iniciar el timer
    lastResetRef.current = Date.now();
    resetTimer();

    // Cleanup
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearAllTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]); // Solo depende de enabled

  return {
    remainingTime,
    isWarning,
    resetTimer,
    extendSession,
  };
};
